import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", contact_name: "", contact_phone: "", company: "", value: "", stage: "", status_id: null,
  follow_up_at: "", owner_id: "", notes: "", chat_id: null };
const toLocal = (iso) => (iso ? new Date(iso).toLocaleString("sv-SE").slice(0, 16).replace(" ", "T") : "");

export const StatusDots = ({ statuses, value, onChange }) => (
  <div className="flex flex-wrap gap-2" data-testid="opp-status-dots">
    {statuses.map((s) => (
      <button key={s.id} type="button" onClick={() => onChange(value === s.id ? null : s.id)} title={s.name}
              data-testid={`opp-status-${s.id}`}
              className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border transition-colors ${value === s.id ? "border-foreground bg-muted font-semibold" : "border-border hover:bg-muted"}`}>
        <span className="w-3 h-3 rounded-full" style={{ background: s.color }} /> {s.name}
      </button>
    ))}
  </div>
);

export const OpportunityDialog = ({ open, onOpenChange, initial, stages, statuses, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [users, setUsers] = useState([]);
  const editing = !!initial?.id;

  useEffect(() => {
    if (!open) return;
    setF({ ...EMPTY, stage: (stages.find((s) => s.kind === "open") || stages[0])?.id || "", ...initial, value: initial?.value ?? "", follow_up_at: toLocal(initial?.follow_up_at),
           owner_id: initial?.owner_id || "" });
    api.get("/users/directory").then(({ data }) => setUsers(data)).catch(() => {});
  }, [open, initial, stages]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.title.trim()) return toast.error("Informe o título");
    const body = { title: f.title, contact_name: f.contact_name || null, contact_phone: (f.contact_phone || "").replace(/\D/g, "") || null,
      company: f.company || null, value: parseFloat(String(f.value).replace(",", ".")) || 0, stage: f.stage, status_id: f.status_id || null,
      follow_up_at: f.follow_up_at ? new Date(f.follow_up_at).toISOString() : null, owner_id: f.owner_id || null,
      notes: f.notes || null, chat_id: f.chat_id || null };
    try {
      if (editing) await api.patch(`/opportunities/${initial.id}`, body);
      else await api.post("/opportunities", body);
      toast.success(editing ? "Oportunidade atualizada" : "Oportunidade criada no CRM");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar"); }
  };

  const remove = async () => {
    if (!window.confirm("Excluir esta oportunidade?")) return;
    await api.delete(`/opportunities/${initial.id}`);
    onOpenChange(false);
    onSaved?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="opp-dialog">
        <DialogHeader><DialogTitle>{editing ? "Editar oportunidade" : "Nova oportunidade"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Título</Label><Input value={f.title} onChange={set("title")} placeholder="Ex.: Orçamento de 50 unidades" data-testid="opp-title-field" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Contato</Label><Input value={f.contact_name || ""} onChange={set("contact_name")} data-testid="opp-contact-field" /></div>
            <div><Label>Telefone</Label><Input value={f.contact_phone || ""} onChange={set("contact_phone")} data-testid="opp-phone-field" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Empresa</Label><Input value={f.company || ""} onChange={set("company")} data-testid="opp-company-field" /></div>
            <div><Label>Valor (R$)</Label><Input value={f.value} onChange={set("value")} inputMode="decimal" data-testid="opp-value-field" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Coluna do funil</Label>
              <select value={f.stage} onChange={set("stage")} className="w-full h-10 mt-1 border border-border rounded-md bg-background px-2 text-sm" data-testid="opp-stage-select">
                {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Responsável</Label>
              <select value={f.owner_id} onChange={set("owner_id")} className="w-full h-10 mt-1 border border-border rounded-md bg-background px-2 text-sm" data-testid="opp-owner-select">
                <option value="">Eu mesmo</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <div><Label>Status</Label><div className="mt-1"><StatusDots statuses={statuses} value={f.status_id} onChange={(v) => setF({ ...f, status_id: v })} /></div></div>
          <div>
            <Label>Retornar contato em</Label>
            <Input type="datetime-local" value={f.follow_up_at} onChange={set("follow_up_at")} className="mt-1" data-testid="opp-followup-field" />
          </div>
          <div><Label>Observações</Label><Textarea value={f.notes || ""} onChange={set("notes")} rows={3} data-testid="opp-notes-field" /></div>
        </div>
        <DialogFooter className="gap-2">
          {editing && <Button variant="ghost" className="text-destructive mr-auto" onClick={remove} data-testid="opp-delete-button"><Trash2 className="w-4 h-4 mr-1" /> Excluir</Button>}
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="opp-save-button">{editing ? "Salvar" : "Criar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
